import { TPSScores } from '../types/tps.types';
import {
  signedDifferentialScore,
  softmaxRankings,
  inferredConfidence,
  userTraitMean,
  type DifferentialTraitSet,
} from './utils';

export interface HogwartsHouseDetail {
  house: 'gryffindor' | 'hufflepuff' | 'ravenclaw' | 'slytherin';
  houseName: string;
  /** Leader's softmax percentage (0-100). */
  score: number;
  confidence: number;
  confidenceLabel: 'Low' | 'Medium' | 'High';
  /** True when no house clearly dominates — UI should surface "Mixed". */
  mixed: boolean;
  /** True when leader and runner-up are statistically tied. */
  noSignal: boolean;
  secondary: string;
  /** Per-house softmax percentages summing to ~100. */
  houseScores: Record<string, number>;
  traits: string[];
  description: string;
}

// Trait differentials for each Hogwarts house.
//
// Designed to (a) match canon archetypes and (b) avoid the old failure
// mode where Slytherin won for anyone scoring high on Self-Indulgent or
// Extrinsic. Each house now has anti-traits that actively repel it.
export const HOUSE_TRAITS: Record<string, DifferentialTraitSet & { description: string; traits: string[] }> = {
  gryffindor: {
    positive: ['Assertive', 'Direct', 'Dynamic', 'Optimistic', 'Self-Principled'],
    positiveModerate: ['Physical', 'Self-Mastery', 'Extrinsic', 'Optimistic'],
    negative: ['Passive', 'Pessimistic', 'Self-Indulgent'],
    signatureTrait: 'Assertive',
    description: 'Brave, daring, and chivalrous',
    traits: ['Bravery', 'Daring', 'Chivalry', 'Nerve'],
  },
  hufflepuff: {
    positive: ['Communal Navigate', 'Responsive', 'Diplomatic', 'Lawful', 'Social'], // TPS-EXEMPT: Social=processing.reality.social
    positiveModerate: ['Structured', 'Realistic', 'Modular'],
    negative: ['Independent Navigate', 'Self-Indulgent', 'Stoic'],
    signatureTrait: 'Communal Navigate',
    description: 'Loyal, patient, and fair',
    traits: ['Loyalty', 'Patience', 'Hard Work', 'Fairness'],
  },
  ravenclaw: {
    // Re-keyed against the 12-persona house corpus, where all three Ravenclaw
    // personas reported Slytherin: `Intuitive` measured LOW for them (they are
    // methodical, not hunch-driven) and `Self-Aware` is a triad middle, so the
    // house was effectively carried by traits Slytherin shares. Intrinsic
    // motivation (learning for its own sake) is the discriminator against
    // Slytherin's externally-referenced ambition.
    positive: ['Analytical', 'Intrinsic', 'Self-Principled', 'Independent', 'Universal'], // TPS-EXEMPT: Independent=interpersonal.navigate.independent
    positiveModerate: ['Self-Aware', 'Intuitive', 'Varied'],
    negative: ['Physical', 'Extrinsic', 'Communal Navigate'],
    signatureTrait: 'Analytical',
    description: 'Wise, creative, and curious',
    traits: ['Wisdom', 'Creativity', 'Learning', 'Wit'],
  },
  slytherin: {
    // Slytherin = ambition + cunning + self-direction. We deliberately
    // do NOT reward Self-Indulgent (the canon trait is resourcefulness,
    // not hedonism) and we penalize the warm/communal cluster.
    // Stoic demoted to moderate — it was over-weighted and dragged
    // pure analysts (Ravenclaw-leaning) into Slytherin.
    positive: ['Self-Mastery', 'Pragmatic', 'Self-Principled', 'Independent Navigate'],
    positiveModerate: ['Assertive', 'Analytical', 'Structured', 'Stoic'],
    // `Universal` and `Intrinsic` are the anti-traits that stop a disciplined
    // analyst (Ravenclaw) from landing here: canon Slytherin ambition is
    // concrete and externally referenced, not abstract or self-rewarding.
    // Without them the house kept Analytical/Self-Mastery-high Ravenclaws.
    negative: ['Communal Navigate', 'Diplomatic', 'Optimistic', 'Responsive', 'Universal', 'Intrinsic'],
    signatureTrait: 'Self-Mastery',
    description: 'Ambitious, cunning, and resourceful',
    traits: ['Ambition', 'Cunning', 'Resourcefulness', 'Leadership'],
  },
};

export function calculateHogwartsHouse(scores: TPSScores): HogwartsHouseDetail {
  const mean = userTraitMean(scores);

  const raw = (Object.entries(HOUSE_TRAITS) as Array<[
    keyof typeof HOUSE_TRAITS,
    typeof HOUSE_TRAITS[keyof typeof HOUSE_TRAITS]
  ]>).map(([house, traits]) => ({
    key: house,
    score: signedDifferentialScore(scores, traits, mean),
    tiebreak: traits.signatureTrait ? (scores[traits.signatureTrait] ?? mean) - mean : 0,
  }));

  const ranked = softmaxRankings(raw);
  const { confidence, confidenceLabel, mixed, noSignal } = inferredConfidence(ranked);

  const primaryHouse = ranked[0].key as HogwartsHouseDetail['house'];
  const secondary = ranked[1]?.key ?? primaryHouse;
  const houseConfig = HOUSE_TRAITS[primaryHouse];

  const houseScores: Record<string, number> = {};
  for (const r of ranked) houseScores[r.key] = r.percentage;

  return {
    house: primaryHouse,
    houseName: primaryHouse.charAt(0).toUpperCase() + primaryHouse.slice(1),
    score: ranked[0].percentage,
    confidence,
    confidenceLabel,
    mixed,
    noSignal,
    secondary: secondary.charAt(0).toUpperCase() + secondary.slice(1),
    houseScores,
    traits: houseConfig.traits,
    description: houseConfig.description,
  };
}

// House colors for UI
export const HOUSE_COLORS: Record<string, { primary: string; secondary: string }> = {
  gryffindor: { primary: 'hsl(0, 70%, 45%)', secondary: 'hsl(45, 90%, 50%)' },
  hufflepuff: { primary: 'hsl(45, 90%, 50%)', secondary: 'hsl(0, 0%, 10%)' },
  ravenclaw: { primary: 'hsl(220, 60%, 45%)', secondary: 'hsl(35, 50%, 55%)' },
  slytherin: { primary: 'hsl(140, 50%, 35%)', secondary: 'hsl(0, 0%, 75%)' },
};
